import type { NewsItem } from '@structural-climbing/ui'

// Event times are stored as wall clock ("YYYY-MM-DDTHH:MM[:SS]"), so they parse
// as local time — the clock the gym and its members share. Plain news only has a
// date; treat it as the whole day so an all-day event stays "upcoming" until
// midnight.
function toTime(value: string, endOfDay = false): number {
  const iso = value.includes('T') ? value : `${value}T${endOfDay ? '23:59:59' : '00:00:00'}`
  return new Date(iso).getTime()
}

function startTime(item: NewsItem): number {
  return toTime(item.starts_at || item.news_date)
}

function endTime(item: NewsItem): number {
  return toTime(item.ends_at || item.starts_at || item.news_date, true)
}

export function isUpcoming(item: NewsItem, now: number = Date.now()): boolean {
  return Boolean(item.is_event) && endTime(item) >= now
}

// Upcoming events lead the list, soonest first, so the next thing happening is
// what members see; everything else follows newest-first.
export function sortNews(items: NewsItem[], now: number = Date.now()): NewsItem[] {
  const { upcoming, past } = partitionEvents(items, now)
  return [...upcoming, ...past]
}

// The Events page renders the two groups under their own headings, so it needs
// them split rather than concatenated. Both come back sorted the way they are
// displayed: what happens next first, then the most recent of what is over.
export function partitionEvents(
  items: NewsItem[],
  now: number = Date.now(),
): { upcoming: NewsItem[]; past: NewsItem[] } {
  const upcoming: NewsItem[] = []
  const past: NewsItem[] = []
  for (const item of items) (isUpcoming(item, now) ? upcoming : past).push(item)

  upcoming.sort((a, b) => startTime(a) - startTime(b))
  past.sort((a, b) => startTime(b) - startTime(a))
  return { upcoming, past }
}

// The soonest upcoming event, which the list badges as "NEXT".
export function nextEventId(items: NewsItem[], now: number = Date.now()): string | null {
  const upcoming = items.filter((i) => isUpcoming(i, now))
  if (!upcoming.length) return null
  return upcoming.reduce((a, b) => (startTime(a) <= startTime(b) ? a : b)).id
}
